/** Waste Management Dashboard (Demo Mode only) - operational waste analytics for a multi-site
 * foodservice/hospitality/retail operator. Helps operational, procurement and sustainability
 * teams see where waste is occurring, why, what it costs and which actions reduce it. No
 * backend waste analytics/reporting feed exists yet - the real production feature is the Daily
 * Waste Checksheet (`/store/waste-logs`), which captures individual entries but has no
 * aggregated reporting; all figures here are static mock data (see `data.ts`), matching the
 * "seeded illustrative numbers" approach used by FinanceDashboard/LabourManagement. */

import { useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import FiltersBar, { ALL_FILTER_VALUE, initialWasteFilters } from './components/FiltersBar';
import KpiCard from './components/KpiCard';
import OverviewTab from './components/OverviewTab';
import BreakdownTab from './components/BreakdownTab';
import ReasonsTab from './components/ReasonsTab';
import LifecycleTab from './components/LifecycleTab';
import FinancialTab from './components/FinancialTab';
import EnvironmentalTab from './components/EnvironmentalTab';
import ForecastingTab from './components/ForecastingTab';
import InventoryExpiryTab from './components/InventoryExpiryTab';
import SupplierPerformanceTab from './components/SupplierPerformanceTab';
import SiteComparisonTab from './components/SiteComparisonTab';
import MenuRecipeTab from './components/MenuRecipeTab';
import ActionsTab from './components/ActionsTab';
import AlertsDataQualityTab from './components/AlertsDataQualityTab';
import { EXECUTIVE_KPI_CARDS, SITE_GROUP, SITE_REGION, SITE_WASTE, WASTE_ACTIONS } from './data';
import { WasteActionItem, WasteDashboardTab, WasteFilters } from './types';

const TABS: { key: WasteDashboardTab; labelKey: string; label: string }[] = [
  { key: 'overview', labelKey: 'wasteDashboardTabOverview', label: 'Executive overview' },
  { key: 'breakdown', labelKey: 'wasteDashboardTabBreakdown', label: 'Waste breakdown' },
  { key: 'reasons', labelKey: 'wasteDashboardTabReasons', label: 'Waste reasons' },
  { key: 'lifecycle', labelKey: 'wasteDashboardTabLifecycle', label: 'Lifecycle' },
  { key: 'financial', labelKey: 'wasteDashboardTabFinancial', label: 'Financial impact' },
  { key: 'environmental', labelKey: 'wasteDashboardTabEnvironmental', label: 'Environmental impact' },
  { key: 'forecasting', labelKey: 'wasteDashboardTabForecasting', label: 'Forecasting & production' },
  { key: 'inventory', labelKey: 'wasteDashboardTabInventory', label: 'Inventory & expiry risk' },
  { key: 'supplier', labelKey: 'wasteDashboardTabSupplier', label: 'Supplier performance' },
  { key: 'siteComparison', labelKey: 'wasteDashboardTabSiteComparison', label: 'Site comparison' },
  { key: 'menuRecipe', labelKey: 'wasteDashboardTabMenuRecipe', label: 'Menu & recipe analysis' },
  { key: 'actions', labelKey: 'wasteDashboardTabActions', label: 'Waste reduction actions' },
  { key: 'alertsDataQuality', labelKey: 'wasteDashboardTabAlertsDataQuality', label: 'Alerts & data quality' },
];

const NEXT_ACTION_STATUS: Record<WasteActionItem['status'], WasteActionItem['status']> = {
  'Not started': 'In progress',
  'In progress': 'Completed',
  Overdue: 'In progress',
  Completed: 'Completed',
};

const WasteManagementDashboard = () => {
  const { t } = useTranslation('common');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [filters, setFilters] = useState<WasteFilters>(initialWasteFilters);
  const [activeTab, setActiveTab] = useState<WasteDashboardTab>('overview');
  const [actions, setActions] = useState<WasteActionItem[]>(WASTE_ACTIONS);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSites = useMemo(() => SITE_WASTE.filter((row) => (filters.site === ALL_FILTER_VALUE || row.site === filters.site)
    && (filters.region === ALL_FILTER_VALUE || SITE_REGION[row.site] === filters.region)
    && (filters.siteGroup === ALL_FILTER_VALUE || SITE_GROUP[row.site] === filters.siteGroup)
    && (filters.performanceBand === ALL_FILTER_VALUE || row.performanceBand === filters.performanceBand)), [filters]);

  const onAdvanceActionStatus = (id: string) => setActions((prev) => prev.map((action) => (action.id === id
    ? { ...action, status: NEXT_ACTION_STATUS[action.status], resultAfterCompletion: NEXT_ACTION_STATUS[action.status] === 'Completed' ? 'Marked complete - awaiting next period\u2019s figures to confirm saving.' : action.resultAfterCompletion }
    : action)));

  return (
    <PageArea>
      <PageTitle subTitle={t('wasteDashboardSubtitle', 'Understand where food waste is occurring, why it is happening, what it costs and which actions will reduce it.')}>
        {t('wasteDashboardTitle', 'Waste Management Dashboard')}
      </PageTitle>

      <FiltersBar filters={filters}
        onChange={setFilters} />

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardExecutiveSummary', 'Executive summary')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {EXECUTIVE_KPI_CARDS.map((card) => (
              <KpiCard key={card.key}
                data={card} />
            ))}
          </Box>
        </Box>
      </Section>

      <Box sx={{ mt: 6 }}>
        <Tabs value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto">
          {TABS.map((tab) => (
            <Tab key={tab.key}
              value={tab.key}
              label={t(tab.labelKey, tab.label)} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mt: 3 }}>
        {activeTab === 'overview' && (
          <OverviewTab currencyCode={currencyCode}
            actions={actions}
            onNavigateToActions={() => setActiveTab('actions')} />
        )}
        {activeTab === 'breakdown' && (
          <BreakdownTab sites={filteredSites}
            currencyCode={currencyCode} />
        )}
        {activeTab === 'reasons' && (
          <ReasonsTab currencyCode={currencyCode} />
        )}
        {activeTab === 'lifecycle' && (
          <LifecycleTab />
        )}
        {activeTab === 'financial' && (
          <FinancialTab currencyCode={currencyCode} />
        )}
        {activeTab === 'environmental' && (
          <EnvironmentalTab />
        )}
        {activeTab === 'forecasting' && (
          <ForecastingTab currencyCode={currencyCode} />
        )}
        {activeTab === 'inventory' && (
          <InventoryExpiryTab />
        )}
        {activeTab === 'supplier' && (
          <SupplierPerformanceTab currencyCode={currencyCode} />
        )}
        {activeTab === 'siteComparison' && (
          <SiteComparisonTab sites={filteredSites}
            currencyCode={currencyCode} />
        )}
        {activeTab === 'menuRecipe' && (
          <MenuRecipeTab currencyCode={currencyCode} />
        )}
        {activeTab === 'actions' && (
          <ActionsTab actions={actions}
            currencyCode={currencyCode}
            onAdvanceStatus={onAdvanceActionStatus} />
        )}
        {activeTab === 'alertsDataQuality' && (
          <AlertsDataQualityTab />
        )}
      </Box>
    </PageArea>
  );
};

export default WasteManagementDashboard;
